
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { MessageCircle, BookOpen, FileText, HelpCircle, Trash2, FolderOpen, Building } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useProject } from "@/contexts/ProjectContext";
import { supabase } from "@/integrations/supabase/client";
import NavBar from "@/components/NavBar";

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export default function Ask() {
  const [question, setQuestion] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversation, setConversation] = useState<Message[]>([]);
  const { currentProject } = useProject();
  const { toast } = useToast();

  const quickTopics = [
    "HM Treasury Green Book",
    "5 Case Model 2015",
    "Strategic Outline Case",
    "Outline Business Case",
    "Full Business Case",
    "Economic Assessment",
    "Value for Money",
    "Risk Management",
    "Procurement Strategy"
  ];

  const clearConversation = () => {
    setConversation([]);
    toast({
      title: "Conversation Cleared",
      description: "Your conversation history has been cleared.",
    });
  };

  const handleAskCasey = async () => {
    if (!question.trim()) return;

    setIsLoading(true);
    
    const userMessage: Message = {
      role: 'user',
      content: question,
      timestamp: new Date()
    };
    
    setConversation(prev => [...prev, userMessage]);
    console.log("Asking Casey:", question);

    try {
      const { data, error } = await supabase.functions.invoke('openai-chat', {
        body: {
          messages: conversation.map(msg => ({ role: msg.role, content: msg.content })),
          question: question
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to get response from Casey');
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to get response from Casey');
      }

      const assistantMessage: Message = {
        role: 'assistant',
        content: data.content,
        timestamp: new Date()
      };

      setConversation(prev => [...prev, assistantMessage]);
      setQuestion("");
      
    } catch (error) {
      console.error('Error calling Casey:', error);
      toast({
        title: "Error",
        description: "Failed to get response from Casey. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb Navigation */}
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/projects-selector" className="flex items-center gap-2">
                  <FolderOpen className="h-4 w-4" />
                  Portfolio
                </BreadcrumbLink>
              </BreadcrumbItem>
              {currentProject && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink href="/dashboard" className="flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      {currentProject.name}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </>
              )}
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Ask Casey</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Ask Casey</h1>
            <p className="text-muted-foreground">
              Your AI assistant for HM Treasury guidance, Green Book compliance, and business case development
              {currentProject && (
                <span className="block mt-1 text-sm">
                  Working on: <span className="font-medium">{currentProject.name}</span>
                </span>
              )}
            </p>
          </div>

          {/* Conversation History */}
          {conversation.length > 0 && (
            <Card className="mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Conversation with Casey</CardTitle>
                <Button variant="outline" size="sm" onClick={clearConversation}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear History
                </Button>
              </CardHeader>
              <CardContent className="space-y-4 max-h-96 overflow-y-auto">
                {conversation.map((message, index) => (
                  <div key={index} className={`p-4 rounded-lg ${
                    message.role === 'user' 
                      ? 'bg-primary/10 ml-8' 
                      : 'bg-muted mr-8'
                  }`}>
                    <div className="flex justify-between items-start mb-2">
                      <strong className="text-sm">
                        {message.role === 'user' ? 'You' : 'Casey'}
                      </strong>
                      <span className="text-xs text-muted-foreground">
                        {message.timestamp.toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Main Question Interface */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Ask Your Question
              </CardTitle>
              <CardDescription>
                Get expert guidance on HM Treasury processes, Green Book requirements, and business case development
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Ask about HM Treasury guidance, Green Book compliance, business case development, or any related topic..."
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                className="min-h-32"
              />
              <Button 
                onClick={handleAskCasey} 
                disabled={!question.trim() || isLoading}
                className="w-full"
              >
                {isLoading ? "Casey is thinking..." : "Ask Casey"}
              </Button>
            </CardContent>
          </Card>

          {/* Quick Topics */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                Quick Topics
              </CardTitle>
              <CardDescription>
                Click on any topic to get started quickly
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {quickTopics.map((topic) => (
                  <Badge
                    key={topic}
                    variant="secondary"
                    className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                    onClick={() => setQuestion(`Tell me about ${topic}`)}
                  >
                    {topic}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  HM Treasury Guidance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Get instant access to comprehensive HM Treasury guidance including the 2022 Green Book updates
                </p>
                <Button variant="outline" className="w-full">
                  Browse Guidance
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  5 Case Model Support
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Expert assistance with Strategic, Economic, Commercial, Financial, and Management cases
                </p>
                <Button variant="outline" className="w-full">
                  Learn More
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
